import { useState, useRef, ChangeEvent } from 'react';

interface LogoUploadProps {
  onUpload: (file: File) => Promise<void>;
  currentLogoPath?: string | null;
  isUploading?: boolean;
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];
const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB

export function LogoUpload({ onUpload, currentLogoPath, isUploading = false }: LogoUploadProps) {
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    setError(null);
    const file = e.target.files?.[0];

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Only PNG and JPEG images are accepted');
      setSelectedFile(null);
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      setError('File size must not exceed 2 MB');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  }

  async function handleUpload() {
    if (!selectedFile) return;
    setError(null);

    try {
      await onUpload(selectedFile);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  }

  return (
    <div className="logo-upload">
      <label htmlFor="logo-file" className="form__label">Company Logo</label>

      {currentLogoPath && (
        <p className="logo-upload__current">
          Current logo: <code>{currentLogoPath}</code>
        </p>
      )}

      <div className="logo-upload__controls">
        <input
          id="logo-file"
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg"
          onChange={handleFileChange}
          aria-describedby={error ? 'logo-error' : 'logo-hint'}
        />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>

      <span id="logo-hint" className="form__hint">
        PNG or JPEG, max 2 MB
      </span>

      {error && (
        <span id="logo-error" className="form__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
